import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const Ctx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("venvay_token"));
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("venvay_cart") || "[]"); }
    catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("venvay_wishlist") || "[]"); }
    catch { return []; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("venvay_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("venvay_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("venvay_token"); setToken(null);
    }).finally(() => setLoading(false));
  }, [token]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("venvay_token", r.data.token);
    setToken(r.data.token); setUser(r.data.user);
    return r.data.user;
  };
  const signup = async (name, email, password, phone) => {
    const r = await api.post("/auth/signup", { name, email, password, phone });
    localStorage.setItem("venvay_token", r.data.token);
    setToken(r.data.token); setUser(r.data.user);
    return r.data.user;
  };
  const logout = () => {
    localStorage.removeItem("venvay_token");
    setToken(null); setUser(null);
  };

  const addToCart = useCallback((product, size = "M", qty = 1, color = null) => {
    setCart((prev) => {
      const key = `${product.id}-${size}-${color || ""}`;
      const idx = prev.findIndex((i) => `${i.product_id}-${i.size}-${i.color || ""}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, {
        product_id: product.id, name: product.name, price: product.price,
        image: product.images[0], qty, size, color,
      }];
    });
  }, []);

  const updateCartQty = (idx, qty) => setCart((p) => {
    if (qty <= 0) return p.filter((_, i) => i !== idx);
    const n = [...p]; n[idx] = { ...n[idx], qty }; return n;
  });
  const removeFromCart = (idx) => setCart((p) => p.filter((_, i) => i !== idx));
  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const isWished = useCallback((id) => wishlist.some((x) => x.id === id), [wishlist]);
  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      if (prev.some((x) => x.id === product.id)) return prev.filter((x) => x.id !== product.id);
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.images?.[0] || product.image, subcategory: product.subcategory }];
    });
  }, []);
  const wishlistCount = wishlist.length;

  return (
    <Ctx.Provider value={{ user, token, login, signup, logout, cart, addToCart, updateCartQty, removeFromCart, clearCart, cartCount, wishlist, toggleWishlist, isWished, wishlistCount, loading }}>
      {children}
    </Ctx.Provider>
  );
}

export const useApp = () => useContext(Ctx);
